import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent
} from '@mui/material';
import { styled } from '@mui/system';
import { FaEye, FaEdit, FaCheck, FaTimes, FaSearch, FaFilter, FaStore, FaClock } from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { getVendors, updateVendorStatus, approveVendor, getVendorDetails } from '../../services/api';

const Container = styled(Box)({
    padding: '2rem',
});

const PageTitle = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '2rem',
});

const StatsCard = styled(Card)({
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
});

const VendorManagement = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);

    useEffect(() => {
        fetchVendors();
    }, []);

    const fetchVendors = async () => {
        try {
            const response = await getVendors({ search: searchTerm, status: statusFilter });
            setVendors(response.vendors || []);
        } catch (err) {
            setError('Failed to fetch vendors');
            console.error('Error fetching vendors:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (vendorId, newStatus) => {
        try {
            await updateVendorStatus(vendorId, newStatus);
            fetchVendors();
        } catch (err) {
            setError('Failed to update vendor status');
        }
    };

    const handleApproveVendor = async (vendorId) => {
        try {
            await approveVendor(vendorId);
            fetchVendors();
        } catch (err) {
            setError('Failed to approve vendor');
        }
    };

    const handleViewDetails = async (vendor) => {
        setDetailsLoading(true);
        try {
            const details = await getVendorDetails(vendor.id);
            setSelectedVendor(details);
            setDialogOpen(true);
        } catch (err) {
            setError('Failed to fetch vendor details');
        } finally {
            setDetailsLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return AppColors.successGreen;
            case 'pending': return AppColors.warningOrange;
            case 'suspended': return AppColors.errorRed;
            case 'approved': return AppColors.successGreen;
            default: return AppColors.neutralMedium;
        }
    };

    const getVendorStats = () => {
        const total = vendors.length;
        const active = vendors.filter(v => v.status === 'active').length;
        const pending = vendors.filter(v => v.status === 'pending').length;
        const suspended = vendors.filter(v => v.status === 'suspended').length;
        
        return { total, active, pending, suspended };
    };

    const stats = getVendorStats();

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <PageTitle>Vendor Management</PageTitle>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ 
                                    p: 1, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${AppColors.primaryDark}20`,
                                    mr: 2 
                                }}>
                                    <FaStore color={AppColors.primaryDark} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" color={AppColors.primaryDark}>
                                        {stats.total}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Vendors
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ 
                                    p: 1, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${AppColors.successGreen}20`,
                                    mr: 2 
                                }}>
                                    <FaCheck color={AppColors.successGreen} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" color={AppColors.primaryDark}>
                                        {stats.active}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Active
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ 
                                    p: 1, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${AppColors.warningOrange}20`,
                                    mr: 2 
                                }}>
                                    <FaClock color={AppColors.warningOrange} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" color={AppColors.primaryDark}>
                                        {stats.pending}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Pending
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Box sx={{ 
                                    p: 1, 
                                    borderRadius: '50%', 
                                    backgroundColor: `${AppColors.errorRed}20`,
                                    mr: 2 
                                }}>
                                    <FaTimes color={AppColors.errorRed} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" color={AppColors.primaryDark}>
                                        {stats.suspended}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Suspended
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StatsCard>
                </Grid>
            </Grid>

            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box display="flex" gap={2} mb={3}>
                    <TextField
                        placeholder="Search vendors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <FaSearch style={{ marginRight: 8 }} />,
                        }}
                        sx={{ flexGrow: 1 }}
                    />
                    <TextField
                        select
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 120 }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                    </TextField>
                    <Button
                        variant="contained"
                        onClick={fetchVendors}
                        startIcon={<FaFilter />}
                        sx={{
                            backgroundColor: AppColors.primaryDark,
                            '&:hover': { backgroundColor: AppColors.primaryYellow, color: AppColors.primaryDark }
                        }}
                    >
                        Filter
                    </Button>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Company Name</TableCell>
                                <TableCell>Contact Person</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vendors.map((vendor) => (
                                <TableRow key={vendor.id}>
                                    <TableCell>{vendor.name || vendor.company_name}</TableCell>
                                    <TableCell>{vendor.contact_name || 'N/A'}</TableCell>
                                    <TableCell>{vendor.email}</TableCell>
                                    <TableCell>{vendor.phone || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={vendor.status} 
                                            sx={{ 
                                                backgroundColor: `${getStatusColor(vendor.status)}20`,
                                                color: getStatusColor(vendor.status)
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(vendor.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton 
                                            onClick={() => handleViewDetails(vendor)}
                                            disabled={detailsLoading}
                                        >
                                            <FaEye />
                                        </IconButton>
                                        {vendor.status === 'pending' && (
                                            <IconButton 
                                                onClick={() => handleApproveVendor(vendor.id)}
                                                color="success"
                                            >
                                                <FaCheck />
                                            </IconButton>
                                        )}
                                        <IconButton 
                                            onClick={() => handleStatusChange(
                                                vendor.id, 
                                                vendor.status === 'active' ? 'suspended' : 'active'
                                            )}
                                            color={vendor.status === 'active' ? 'error' : 'success'}
                                        >
                                            <FaEdit />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Vendor Details Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Vendor Details</DialogTitle>
                <DialogContent>
                    {selectedVendor && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Company Information
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Name: {selectedVendor.name || selectedVendor.company_name}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Contact Person: {selectedVendor.contact_name || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Email: {selectedVendor.email}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Phone: {selectedVendor.phone || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Location & Status
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Address: {selectedVendor.address || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    City: {selectedVendor.city || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Emirate: {selectedVendor.emirate || 'N/A'}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    Status: {selectedVendor.status}
                                </Typography>
                            </Grid>
                            {selectedVendor.buying_items && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Buying Items
                                    </Typography>
                                    <Typography variant="body2">
                                        {Array.isArray(selectedVendor.buying_items) 
                                            ? selectedVendor.buying_items.join(', ')
                                            : selectedVendor.buying_items}
                                    </Typography>
                                </Grid>
                            )}
                            {selectedVendor.description && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Description
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedVendor.description}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default VendorManagement;
